import { useEffect, useMemo, useState } from "react";
import { createAdminComplaint, getComplaintOverview, updateComplaint } from "../api/instateamAdminApi";
import { AppIcon, MetricCard } from "../components/InstateamMasterComponents";
import { toAbsoluteUrl } from "../utils/adminHelpers";

const STATUS_OPTIONS = [
  ["all", "All Status"],
  ["open", "Open"],
  ["in_progress", "In Progress"],
  ["resolved", "Resolved"],
  ["closed", "Closed"],
];

const PRIORITY_OPTIONS = [
  ["all", "All Priority"],
  ["low", "Low"],
  ["medium", "Medium"],
  ["high", "High"],
];

const CATEGORY_OPTIONS = [
  ["all", "All Categories"],
  ["IT & Systems", "IT & Systems"],
  ["Facilities", "Facilities"],
  ["HR", "HR"],
  ["Payroll", "Payroll"],
  ["Documents", "Documents"],
  ["Other", "Other"],
];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatLabel(value) {
  return String(value || "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function EmployeeAvatar({ employee }) {
  const photoUrl = employee?.profilePhoto?.url ? toAbsoluteUrl(employee.profilePhoto.url) : "";
  if (photoUrl) {
    return <img src={photoUrl} alt={employee.fullName || "Employee"} className="complaint-avatar" />;
  }

  const initials = String(employee?.fullName || "E")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return <span className="complaint-avatar fallback">{initials || "E"}</span>;
}

export default function ComplaintsPage({
  token,
  employees = [],
  searchText = "",
  onSearchChange = () => {},
  onProtectedError,
  onNotify,
}) {
  const [complaints, setComplaints] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [filters, setFilters] = useState({ status: "all", priority: "all", category: "all" });
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [isCreatingComplaint, setIsCreatingComplaint] = useState(false);
  const [editor, setEditor] = useState({ status: "open", assignedTo: "", resolutionNote: "" });
  const [createForm, setCreateForm] = useState({
    employeeId: "",
    subject: "",
    category: "IT & Systems",
    priority: "medium",
    description: "",
    assignedTo: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchComplaints = async ({ quiet = false } = {}) => {
    if (!token) return;
    try {
      if (!quiet) setIsLoading(true);
      const payload = await getComplaintOverview(token, { ...filters, search: searchText });
      setComplaints(payload.complaints || []);
      setMetrics(payload.metrics || null);
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        onProtectedError?.(error);
        return;
      }
      onNotify?.({ type: "error", text: error.message });
    } finally {
      if (!quiet) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filters.status, filters.priority, filters.category, searchText]);

  useEffect(() => {
    if (!token) return undefined;
    const refreshComplaints = () => {
      if (!document.hidden) fetchComplaints({ quiet: true });
    };
    const intervalId = window.setInterval(refreshComplaints, 5000);
    window.addEventListener("focus", refreshComplaints);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshComplaints);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filters, searchText]);

  const statCards = useMemo(() => [
    { label: "Total Complaints", value: metrics?.total || 0, helper: "All time", icon: "complaints", tone: "green" },
    { label: "Open", value: metrics?.open || 0, helper: "Needs attention", icon: "chat", tone: "yellow" },
    { label: "In Progress", value: metrics?.inProgress || 0, helper: "In progress", icon: "chat", tone: "blue" },
    { label: "Resolved", value: metrics?.resolved || 0, helper: "Completed", icon: "check", tone: "green" },
    { label: "Closed", value: metrics?.closed || 0, helper: "Closed", icon: "close", tone: "red" },
  ], [metrics]);

  function openEditor(complaint) {
    setIsCreatingComplaint(false);
    setSelectedComplaint(complaint);
    setEditor({
      status: complaint.status || "open",
      assignedTo: complaint.assignedTo || "",
      resolutionNote: complaint.resolutionNote || "",
    });
  }

  function openCreator() {
    setSelectedComplaint(null);
    setIsCreatingComplaint(true);
    setCreateForm({
      employeeId: employees[0]?._id || "",
      subject: "",
      category: "IT & Systems",
      priority: "medium",
      description: "",
      assignedTo: "",
    });
  }

  async function handleCreateComplaint(event) {
    event.preventDefault();
    try {
      setIsSaving(true);
      const payload = await createAdminComplaint(token, createForm);
      setComplaints((items) => [payload.complaint, ...items]);
      setIsCreatingComplaint(false);
      onNotify?.({ type: "success", text: "Complaint created successfully." });
      await fetchComplaints({ quiet: true });
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveComplaint(event) {
    event.preventDefault();
    if (!selectedComplaint?._id) return;

    try {
      setIsSaving(true);
      const payload = await updateComplaint(token, selectedComplaint._id, editor);
      const updatedComplaint = payload.complaint;
      setComplaints((items) =>
        items.map((item) => (item._id === updatedComplaint._id ? updatedComplaint : item))
      );
      setSelectedComplaint(updatedComplaint);
      onNotify?.({ type: "success", text: "Complaint updated successfully." });
      await fetchComplaints({ quiet: true });
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
    } finally {
      setIsSaving(false);
    }
  }

  function downloadCsv() {
    const header = [
      "Complaint ID",
      "Raised By",
      "Employee ID",
      "Subject",
      "Category",
      "Priority",
      "Status",
      "Assigned To",
      "Created On",
    ];
    const rows = complaints.map((complaint) => [
      complaint.complaintId,
      complaint.employee?.fullName,
      complaint.employee?.employeeId,
      complaint.subject,
      complaint.category,
      formatLabel(complaint.priority),
      formatLabel(complaint.status),
      complaint.assignedTo || "Unassigned",
      formatDate(complaint.createdAt),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "instateam-complaints.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="complaints-page">
      <div className="page-action-row">
        <div>
          <h3>Complaints Management</h3>
          <p>Track employee complaints, assign owners, and update resolution status.</p>
        </div>
        <button
          type="button"
          className="primary-action"
          onClick={openCreator}
          disabled={!employees.length}
        >
          <AppIcon name="plus" />
          Add / Assign Complaint
        </button>
      </div>

      <div className="complaint-filter-header">
        <select
          className="document-select"
          value={filters.status}
          onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
        >
          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select
          className="document-select"
          value={filters.priority}
          onChange={(event) => setFilters((current) => ({ ...current, priority: event.target.value }))}
        >
          {PRIORITY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select
          className="document-select"
          value={filters.category}
          onChange={(event) => setFilters((current) => ({ ...current, category: event.target.value }))}
        >
          {CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <label className="inline-search complaint-search">
          <AppIcon name="search" />
          <input
            value={searchText}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by subject or ID..."
          />
        </label>
        <button type="button" className="secondary-action small-action" onClick={() => setFilters({ status: "all", priority: "all", category: "all" })}>
          <AppIcon name="filter" />
          Filter
        </button>
        <button type="button" className="secondary-action small-action" onClick={downloadCsv}>
          <AppIcon name="export" />
          Export
        </button>
      </div>

      <div className="metrics-grid complaint-metrics-grid">
        {statCards.map((card) => <MetricCard key={card.label} {...card} />)}
      </div>

      {isCreatingComplaint ? (
        <form className="data-card complaint-create-form" onSubmit={handleCreateComplaint}>
          <select
            className="document-select"
            value={createForm.employeeId}
            onChange={(event) => setCreateForm((current) => ({ ...current, employeeId: event.target.value }))}
            required
          >
            <option value="">Select Employee</option>
            {employees.map((employee) => (
              <option key={employee._id} value={employee._id}>
                {employee.fullName} ({employee.employeeId})
              </option>
            ))}
          </select>
          <input
            value={createForm.subject}
            onChange={(event) => setCreateForm((current) => ({ ...current, subject: event.target.value }))}
            placeholder="Complaint subject"
            required
          />
          <select
            className="document-select"
            value={createForm.category}
            onChange={(event) => setCreateForm((current) => ({ ...current, category: event.target.value }))}
          >
            {CATEGORY_OPTIONS.filter(([value]) => value !== "all").map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select
            className="document-select"
            value={createForm.priority}
            onChange={(event) => setCreateForm((current) => ({ ...current, priority: event.target.value }))}
          >
            {PRIORITY_OPTIONS.filter(([value]) => value !== "all").map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <input
            value={createForm.assignedTo}
            onChange={(event) => setCreateForm((current) => ({ ...current, assignedTo: event.target.value }))}
            placeholder="Assign to team/person"
          />
          <textarea
            value={createForm.description}
            onChange={(event) => setCreateForm((current) => ({ ...current, description: event.target.value }))}
            placeholder="Complaint description"
            required
          />
          <div className="composer-actions">
            <button type="button" className="secondary-action small-action" onClick={() => setIsCreatingComplaint(false)}>Cancel</button>
            <button type="submit" className="primary-action small-action" disabled={isSaving}>{isSaving ? "Creating..." : "Create"}</button>
          </div>
        </form>
      ) : selectedComplaint ? (
        <form className="data-card complaint-editor" onSubmit={handleSaveComplaint}>
          <div>
            <strong>{selectedComplaint.complaintId}</strong>
            <p>{selectedComplaint.subject}</p>
          </div>
          <select className="document-select" value={editor.status} onChange={(event) => setEditor((current) => ({ ...current, status: event.target.value }))}>
            {STATUS_OPTIONS.filter(([value]) => value !== "all").map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <input
            value={editor.assignedTo}
            onChange={(event) => setEditor((current) => ({ ...current, assignedTo: event.target.value }))}
            placeholder="Assign to team/person"
          />
          <input
            value={editor.resolutionNote}
            onChange={(event) => setEditor((current) => ({ ...current, resolutionNote: event.target.value }))}
            placeholder="Resolution note"
          />
          <button type="submit" className="primary-action small-action" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </button>
        </form>
      ) : null}

      <article className="data-card">
        <div className="responsive-table">
          <table className="admin-table complaint-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Complaint ID</th>
                <th>Raised By</th>
                <th>Subject</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Created On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {complaints.map((complaint, index) => (
                <tr key={complaint._id}>
                  <td>{index + 1}</td>
                  <td>{complaint.complaintId}</td>
                  <td>
                    <div className="complaint-employee-cell">
                      <EmployeeAvatar employee={complaint.employee} />
                      <div>
                        <strong>{complaint.employee?.fullName || "Employee"}</strong>
                        <span>{complaint.employee?.employeeId || "-"}</span>
                      </div>
                    </div>
                  </td>
                  <td className="complaint-subject-cell">{complaint.subject}</td>
                  <td>{complaint.category}</td>
                  <td><span className={`complaint-priority ${complaint.priority}`}>{formatLabel(complaint.priority)}</span></td>
                  <td><span className={`complaint-status ${complaint.status}`}>{formatLabel(complaint.status)}</span></td>
                  <td>{complaint.assignedTo || "Unassigned"}</td>
                  <td>{formatDate(complaint.createdAt)}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" title={complaint.description} onClick={() => window.alert(complaint.description || "No description provided.")}>
                        <AppIcon name="eye" />
                      </button>
                      <button type="button" onClick={() => openEditor(complaint)}>
                        <AppIcon name="edit" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!complaints.length ? (
                <tr>
                  <td colSpan="10" className="documents-empty">
                    {isLoading ? "Loading complaints..." : "No complaints found."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  );
}
